import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useDebouncedValue, useLocalStorageState } from '@components/component-helpers';
import { ColumnSettings, Columns, DataGrid } from '@components/data-grid';
import { RowAction } from '@components/data-grid/data-grid-types';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useInfoModal } from '@components/generic-modals/info-modal';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArchive } from '@components/icons/archive';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconJob } from '@components/icons/job';
import { IconLocation } from '@components/icons/location';
import { IconTrash } from '@components/icons/trash';
import { IconUnarchive } from '@components/icons/unarchive';
import { LocationSearchValue } from '@components/location-search';
import { Pagination, usePagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { CareerSiteLanguageType } from '@locale/locale-store';

import { api } from 'api';
import { useLocationCounts, useLocationList } from 'api/locations/hooks';
import { Location } from 'api/locations/requests';
import { Header } from 'components/headers';
import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { Quickfilter } from 'components/Quickfilter';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

export const Locations = () => {
  const { t } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const [_searchValue, setSearchValue] = useState('');

  const [tabIndex, setTabIndex] = useLocalStorageState({
    key: 'location-active-tab',
    defaultValue: 0,
    valueAsNumber: true,
  });

  const searchValue = useDebouncedValue(_searchValue, 300);
  const countsQuery = useLocationCounts(searchValue);

  return (
    <>
      <Header
        // @ts-ignore
        breadcrumbs={<Breadcrumb items={[{ label: t('company.settings.locations.header') }]} />}
        actions={
          <Stack space={2} orientation="horizontal">
            <ExpandableSearch
              value={_searchValue}
              onChange={setSearchValue}
              placeholder={t('company.settings.locations.search.placeholder')}
            />

            <Button
              text={t('company.settings.locations.add')}
              iconLeft={<IconAddPlus />}
              isDisabled={!hasRolePermission('locations:add')}
              tooltip={
                hasRolePermission('locations:add')
                  ? undefined
                  : t('company.settings.locations.add.disabled')
              }
              onClick={() => showModal('LOCATION_FORM', {})}
            />
          </Stack>
        }
      />
      <PageContent noPadding>
        <Quickfilter
          onChange={setTabIndex}
          defaultIndex={tabIndex}
          items={[
            {
              count: countsQuery.data?.activeCount ?? 0,
              label: t('company.settings.locations.tabs.active'),
              render: () => <LocationsList searchValue={searchValue} isActive={true} />,
            },
            {
              count: countsQuery.data?.inactiveCount ?? 0,
              label: t('company.settings.locations.tabs.inactive'),
              render: () => <LocationsList searchValue={searchValue} isActive={false} />,
            },
          ]}
        />
      </PageContent>
    </>
  );
};

const LocationsList = ({ searchValue, isActive }: { searchValue: string; isActive: boolean }) => {
  const { t } = useStore();
  const [count, setCount] = useState(0);
  const pageSize = 50;
  const { numPages, selectedPage, selectPage } = usePagination({
    pageSize: pageSize,
    defaultPage: 1,
    count,
  });
  const locationsQuery = useLocationList(
    pageSize,
    (selectedPage - 1) * pageSize,
    searchValue,
    setCount,
    isActive,
  );
  const actionsForLocation = useLocationOptions();
  useEffect(() => {
    selectPage(1);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);
  const columns = useMemo<Columns<Location & { title: string }>>(
    () => [
      {
        key: 'title',
        label: t('company.settings.locations.columns.title'),
        render: (location) => {
          return <Text font="regular14" color={'purple100'} text={location.title} />;
        },
      },
      {
        key: 'address',
        label: t('company.settings.locations.columns.address'),
        ratio: 2,
        render: (location) => {
          return <Text font="regular14" text={location.address ?? '-'} />;
        },
      },
      {
        key: 'externalId',
        label: t('company.settings.locations.columns.externalid'),
        render: (location) =>
          location.externalId ? (
            <>{location.externalId}</>
          ) : (
            <Text
              font="regular14"
              color="purple50"
              text={t('company.settings.locations.columns.externalid.none')}
              style={{ display: 'inline' }}
            />
          ),
      },
      {
        key: 'numJobs',
        labelIcon: IconJob,
        type: 'numeric',
        center: true,
        ratio: 0.1,
        label: t('company.settings.locations.columns.numjobs'),
      },
    ],
    [t],
  );

  const [settings, setSettings] = useLocalStorageState<
    ColumnSettings<Location & { title: string }>
  >({
    key: 'locations-columns',
    defaultValue: {
      title: { order: 0, visible: true },
      address: { order: 1, visible: true },
      externalId: { order: 2, visible: false },
      numJobs: { order: 3, visible: true },
    },
  });

  if (locationsQuery.isLoading || !locationsQuery.data?.results) {
    return <Loading />;
  }
  if (locationsQuery.data.count === 0) {
    return (
      <EmptyState
        icon={<IconLocation size={80} />}
        header={t('company.settings.locations.nolocations.header')}
        content={t('company.settings.locations.nolocations.content')}
      />
    );
  }

  return (
    <Stack space={4}>
      <DataGrid
        label="Locations"
        data={locationsQuery.data.results}
        columnSettings={settings}
        onColumnSettingsChange={setSettings}
        columns={columns}
        actions={actionsForLocation}
      />
      <Pagination justify="right" count={numPages} selected={selectedPage} onSelect={selectPage} />
    </Stack>
  );
};

function useLocationOptions() {
  const { t, current } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const queryClient = useQueryClient();
  const showInfo = useInfoModal();
  return useCallback(
    (location: Location) => {
      const options: Array<RowAction<Location>> = [];
      if (hasRolePermission('locations:edit')) {
        options.push({
          label: t('company.settings.locations.actions.edit'),
          icon: <IconEdit />,
          onSelect: (location) =>
            showModal('LOCATION_FORM', {
              locationId: location.id,
              externalId: location.externalId,
              translations: location.translations,
              isActive: location.isActive,
              location: {
                address: location.address ?? '',
                latitude: location.latitude,
                longitude: location.longitude,
              } as LocationSearchValue,
            }),
        });
        options.push({
          label: location.isActive
            ? t('company.settings.locations.actions.deactivate')
            : t('company.settings.locations.actions.activate'),
          icon: location.isActive ? <IconUnarchive /> : <IconArchive />,
          onSelect: async (location) => {
            try {
              await api.locations.edit(
                location.id,
                location.externalId ?? '',
                location.translations,
                {
                  address: location.address ?? undefined,
                  latitude: location.latitude ?? undefined,
                  longitude: location.longitude ?? undefined,
                } as LocationSearchValue,
                !(location.isActive ?? true),
              );
              await current.loadInfo();
              queryClient.invalidateQueries(['locations']);
            } catch (error: any) {
              const data = error?.response?.data;
              const translations = data?.translations;
              if (translations) {
                const conflicts = translations
                  .map((e: { language: string; message: string }) => {
                    const lang = e?.language;
                    const title = location.translations.find((t) => t.language === lang)?.title;
                    return title ? { lang, title } : undefined;
                  })
                  .filter(Boolean) as Array<{ lang: string; title: string }>;

                showInfo({
                  title: t('company.settings.locations.locationconflict.header'),
                  headerIcon: <IconFormIndicatorWarning />,
                  children: (
                    <Stack space={4}>
                      <Text
                        text={t('company.settings.locations.locationconflict.samename')}
                        font="regular14"
                      />
                      {conflicts.map((c, idx) => (
                        <Stack key={idx} orientation="horizontal" align="center" space={1}>
                          {FLAG_MAPPER_SMALL[c.lang as CareerSiteLanguageType]}
                          <Text text={c.title} font="semibold14" />
                        </Stack>
                      ))}
                      <Text
                        text={t('company.settings.locations.locationconflict.samename.instruction')}
                        font="regular14"
                      />
                    </Stack>
                  ),
                  buttonText: t('company.settings.locations.locationconflict.close'),
                });
              }
            }
          },
        });
      }
      if (hasRolePermission('locations:delete')) {
        options.push({
          label: t('company.settings.locations.actions.delete'),
          icon: <IconTrash />,
          variant: 'danger',
          isDisabled: (location) => location.numJobs > 0,
          tooltip: (location) =>
            location.numJobs > 0
              ? t('company.settings.locations.actions.delete.disabled')
              : undefined,
          onSelect: (location) => showModal('DELETE_LOCATION', { locationId: location.id }),
        });
      }
      return options;
    },
    [hasRolePermission, t, showModal, queryClient, showInfo, current],
  );
}
